import { Activity, Calendar, Key, Mail, Phone, Shield, User as UserIcon, UserPlus, MoreHorizontal, ToggleLeft, ToggleRight } from "lucide-react";

import ActionMenu from "../../../components/shared/ActionMenu";
import DataTable from "../../../components/shared/DataTable";
import StatusBadge from "../../../components/shared/StatusBadge";
import { Badge } from "../../../components/ui/badge";
import { DropdownMenuItem } from "../../../components/ui/dropdown-menu";
import { Switch } from "../../../components/ui/switch";
import { Label } from "../../../components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "../../../components/ui/popover";
import { formatDate } from "../../../utils/dateUtils";

const UserTable = ({
  users,
  currentUser,
  permissions,
  onEdit,
  onDelete,
  onResetPassword,
  sortBy,
  direction,
  onSort,
  showDeleted,
  setShowDeleted,
  setCurrentPage
}) => {
  const userColumns = [
    {
      header: "User",
      accessor: "name",
      isPrimary: true,
      render: (val, user) => (
        <div className="flex items-center gap-3">
          <div className="p-2 bg-slate-100 dark:bg-yale-blue-800 rounded-lg shrink-0">
            <UserIcon className="w-5 h-5 text-yale-blue-500" />
          </div>
          <div className="flex flex-col min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-yale-blue-900 dark:text-slate-100 truncate max-w-[150px]">
                {user.name}
              </span>
              {user.deleted && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[8px] font-extrabold bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400 uppercase tracking-wider shrink-0">
                  Deleted
                </span>
              )}
              {user.id === currentUser?.id && (
                <Badge
                  variant="secondary"
                  className="text-[9px] py-0 px-1 bg-yale-blue-50 text-yale-blue-600 dark:bg-yale-blue-900/50"
                >
                  You
                </Badge>
              )}
            </div>
            {!user.isActive && <span className="text-[10px] text-red-500 font-medium">Inactive</span>}
          </div>
        </div>
      )
    },
    {
      header: "Role",
      accessor: "role",
      render: (val) => <StatusBadge status={val?.name || val} />
    },
    {
      header: "Tenant",
      accessor: "tenant",
      render: (val, user) => (
        <div className="flex items-center gap-1.5 text-sm text-slate-700 dark:text-slate-300 font-medium">
          <Shield className="w-3.5 h-3.5 text-slate-400" />
          <span>{val?.name || (user.tenantId === "system" ? "System Admin" : "Global")}</span>
        </div>
      )
    },
    {
      header: "Contact Info",
      accessor: "email",
      render: (val, user) => (
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-1.5 text-sm text-slate-700 dark:text-slate-300 font-medium">
            <Mail className="w-3.5 h-3.5 text-slate-400" />
            <span className="truncate max-w-[180px]">{val}</span>
          </div>
          {user.phone && (
            <div className="flex items-center gap-1.5 text-[11px] text-slate-500">
              <Phone className="w-3.5 h-3.5 text-slate-400" />
              {user.phone}
            </div>
          )}
        </div>
      )
    },
    {
      header: "Activity",
      accessor: "lastLogin",
      sortable: true,
      sortKey: "lastLogin",
      render: (val) => (
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-1.5 text-sm text-slate-600 dark:text-slate-400">
            <Activity className="w-3.5 h-3.5 text-yale-blue-500" />
            <span className="text-xs">{val ? `Last login: ${formatDate(val)}` : "Never logged in"}</span>
          </div>
        </div>
      )
    },
    {
      header: "Joined",
      accessor: "createdAt",
      sortable: true,
      sortKey: "createdAt",
      render: (val) => (
        <div className="flex items-center gap-1.5 text-sm text-slate-600 dark:text-slate-400">
          <Calendar className="w-3.5 h-3.5 text-slate-400" />
          {formatDate(val)}
        </div>
      )
    },
    {
      header: "Actions",
      filter: permissions?.canDelete ? (
        <Popover>
          <PopoverTrigger asChild>
            <button
              className={`p-1 rounded-md transition-colors ${showDeleted ? "bg-primary/20 text-primary" : "hover:bg-slate-200 dark:hover:bg-yale-blue-800 text-slate-400"}`}
              onClick={(e) => e.stopPropagation()}
            >
              <MoreHorizontal className="w-4 h-4 hidden md:block" />
              <span className="md:hidden">
                {showDeleted ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
              </span>
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-48 p-3" align="end">
            <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
              <Switch
                checked={showDeleted}
                onCheckedChange={(val) => {
                  setShowDeleted(val);
                  setCurrentPage(0);
                }}
                id="show-deleted-users-table"
              />
              <Label htmlFor="show-deleted-users-table" className="text-xs font-semibold cursor-pointer whitespace-nowrap">
                Show Deleted
              </Label>
            </div>
          </PopoverContent>
        </Popover>
      ) : null,
      accessor: "id",
      render: (_, user) => (
        <ActionMenu
          canEdit={permissions?.canUpdate && !user.deleted}
          canDelete={permissions?.canDelete && user.id !== currentUser?.id && !user.deleted}
          onEdit={() => onEdit(user)}
          onDelete={() => onDelete(user.id)}
          customActions={
            permissions?.canUpdate && !user.deleted && (
              <DropdownMenuItem className="cursor-pointer" onClick={() => onResetPassword(user.id)}>
                <Key className="w-4 h-4 mr-2" />
                <span>Reset Password</span>
              </DropdownMenuItem>
            )
          }
        />
      )
    }
  ];

  return (
    <DataTable
      columns={userColumns}
      data={users}
      emptyStateTitle="No users found"
      emptyStateDescription="Invite team members to collaborate on projects."
      emptyStateIcon={UserPlus}
      sortBy={sortBy}
      direction={direction}
      onSort={onSort}
    />
  );
};

export default UserTable;
